'use client';

import { useMemo } from 'react';

import classnames from 'classnames';
import { es } from 'date-fns/locale/es';
import { default as ReactDatePicker, registerLocale } from 'react-datepicker';

import type { DateCalendarProps } from './DateCalendar.types';

// sort-imports-ignore
import '../date-picker/react-datepicker-vendor.scss';
import styles from '../date-picker/DatePicker.module.scss';

registerLocale('es', es);

function DateCalendar({
  style,
  className,
  highlightedDates = [],
  specialDates = [],
  locale = 'es',
  testId = 'ui-date-calendar',
  ...props
}: DateCalendarProps) {
  // Normalize dates to compare only date part (without time)
  const normalizedHighlightedDates = useMemo(() => {
    return highlightedDates.map((date) => {
      const normalized = new Date(date);
      normalized.setHours(0, 0, 0, 0);
      return normalized;
    });
  }, [highlightedDates]);

  const normalizedSpecialDates = useMemo(() => {
    return specialDates.map((date) => {
      const normalized = new Date(date);
      normalized.setHours(0, 0, 0, 0);
      return normalized;
    });
  }, [specialDates]);

  // Check if a date matches any of the date arrays
  const isDateInArray = (date: Date, dateArray: Date[]): boolean => {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return dateArray.some(
      (arrayDate) => arrayDate.getTime() === normalized.getTime(),
    );
  };

  // Custom day class name - priority: excluded > special > highlighted
  const dayClassName = (date: Date): string => {
    if (isDateInArray(date, normalizedSpecialDates)) {
      return 'special-day';
    }
    if (isDateInArray(date, normalizedHighlightedDates)) {
      return 'highlighted-day';
    }
    return '';
  };

  return (
    <div
      className={classnames(
        styles['wrapper'],
        styles['calendar-view'],
        className,
      )}
      style={style}
      data-testid={testId}
    >
      <ReactDatePicker
        selected={null}
        onChange={() => {
          // Disable date selection - do nothing
        }}
        inline
        disabledKeyboardNavigation
        showPopperArrow={false}
        locale={locale}
        // Highlight specific dates using dayClassName
        dayClassName={dayClassName}
        shouldCloseOnSelect={false}
        {...props}
      />
    </div>
  );
}

export { DateCalendar };
